import { SymptomModes } from '../../symptoms/symptom.model';
`
// export interface SpeicalCaseConfig<T = unknown> {
//   function:
// }`;

export interface CustomPdfConfig<T = unknown> {
  parentName: string;
  filterType?: FilterFields[]; // combe back to it
  filterMode?: 'AND' | 'OR';
  transform?: (data: any, value: any) => any;
  validators?: {
    requiredKeys?: string[];
    checkIfDefault?: (key: string, value: T) => boolean;
  };
}

export type FilterFields =
  | { type: 'include'; value: string; caseSensitive?: boolean }
  | { type: 'exclude'; pattern: string; regex?: boolean }
  | { type: 'ignore'; keys: Set<string>; matchStrategy?: 'exact' | 'partial' }
  | { type: 'custom'; handler: (key: string, value: any) => any }
  | { type: 'not'; filter: FilterFields }
  | { type: 'exists'; keys: Set<string> }
  | { type: 'compare'; comparator: '>' | '<' | '=='; value?: string }
  | { type: 'composite'; operator: 'AND' | 'OR'; filterField: FilterFields[] };

export const customPdf: { [key: string]: CustomPdfConfig } = {
  pieCharts: {
    parentName: 'dryEyeForm',
    filterType: [
      {
        type: 'ignore',
        keys: new Set([
          'assessmentMethod',
          'schemaVersion',
          'osdi',
          'speed',
          'deq',
          'speedii',
          'fitzpatrick',
          'osdi6'
        ]),
        matchStrategy: 'exact'
      }
    ]
  },

  patientCharts: {
    parentName: 'patient',
    filterType: [
      {
        type: 'custom',
        handler: (key: string, value: any) => {
          if (Object.values(value).every(item => item == null)) {
            return;
          }
          const eyeObject = Object.entries(value).reduce((acc, [key, item]) => {
            if (item) {
              acc[key] = item;
            }
            return acc;
          }, {});

          return eyeObject;
        }
      }
    ]
  },

  treatmentPerformed: {
    parentName: 'assessmentForm',
    filterType: [{ type: 'include', value: 'treatmentPerformed' }]
  },
  recommendation: {
    parentName: 'assessmentForm',
    filterType: [{ type: 'include', value: 'recommended' }]
  },
  impressions: {
    parentName: 'assessmentForm',
    filterType: [{ type: 'include', value: 'impressions' }]
  },
  goals: {
    parentName: 'assessmentForm',
    filterType: [{ type: 'include', value: 'goalsForPatient' }]
  },
  discussionSummary: {
    parentName: 'assessmentForm',
    filterType: [
      {
        type: 'custom',
        handler: (key: string, value: any) => {
          const aiScribeResult = value && value.aiScribeResult ? value.aiScribeResult : null;
          const conv =
            aiScribeResult && aiScribeResult.conversationResult
              ? aiScribeResult.conversationResult
              : null;
          const summary = conv && conv.discussionSummary ? conv.discussionSummary : null;
          return summary ? { discussionSummary: summary } : null;
        }
      }
    ]
  },
  signs: {
    parentName: 'dryEyeForm',
    filterType: [
      {
        type: 'ignore',
        keys: new Set([
          'assessmentMethod',
          'schemaVersion',
          'osdi',
          'speed',
          'deq',
          'speedii',
          'fitzpatrick',
          'osdi6'
        ]),
        matchStrategy: 'exact'
      }
    ]
  },
  symptoms: {
    parentName: 'dryEyeForm',
    filterType: [
      {
        type: 'exists',
        keys: new Set(['osdi', 'speed', 'deq', 'speedii', 'fitzpatrick', 'osdi6'])
      }
    ]
  },
  medication: {
    parentName: 'medicalHistoryForm',
    filterType: [{ type: 'exists', keys: new Set(['medications']) }]
  },
  medicalHistory: {
    parentName: 'medicalHistoryForm',
    filterType: [
      {
        type: 'ignore',
        keys: new Set([
          'schemaVersion',
          'medications',
          'pastOcularMedicationsFreeText',
          'patientEnteredMedicationsFreeText',
          'currentOcularMedicationsFreeText'
        ])
      }
    ],
    validators: {
      checkIfDefault: (key, value) => {
        const defaultValueMap = new Map<string, any>([
          ['environmentalSymptoms', true],
          ['eyeItchiness', 0],
          ['eyeTearingQuantity', -10],
          ['symptomFrequency', 0],
          ['symptomSeverity', 0],
          ['symptomDuration', { month: 0, year: 0, total: 0 }]
        ]);

        const defaultValue = defaultValueMap.get(key);
        const assessmentValue = value[key];
        if (defaultValue !== undefined || defaultValue !== null) {
          return typeof defaultValue === 'object'
            ? !Object.entries(assessmentValue).every(([key, value]) => value === defaultValue[key])
            : defaultValue !== assessmentValue;
        }
        return true;
      }
    },
    transform: (data: any, service: any) => {
      let cleanDataEntires = Object.entries(data)
        .filter(([key, value]) => value)
        .reduce((acc, [key, item]) => {
          acc[key] = data[key];
          return acc;
        }, {});
      const dataWithSpecialCondition = {};
      ['environmentalSymptoms', 'otherConditions', 'medicalConditions'].forEach(key => {
        const specialCondition = service.medicalHistoryNormalizedFields[key];
        const filterMap = Object.entries(specialCondition.listConfig.map)
          .filter(([itemKey, item]) => {
            const check = cleanDataEntires[itemKey];
            if (check) {
              delete cleanDataEntires[itemKey];
              return check;
            }
          })
          .reduce((acc, [itemKey, item]) => {
            acc[itemKey] = item;
            return acc;
          }, {});
        if (Object.keys(filterMap).length > 0) {
          dataWithSpecialCondition[key] = filterMap;
          cleanDataEntires = { ...cleanDataEntires, ...dataWithSpecialCondition };
        }
      });
      return cleanDataEntires;
    }
  }
};

export const assessmentTypeKeys = assessmentType => {
  if (Object.keys(assessmentTypeProperty).length < 1) {
    createAssessmentProperties();
  }
  return assessmentTypeProperty[assessmentType];
};

const assessmentTypeProperty: Partial<{ [key in keyof SymptomModes]: any }> = {};

const sharedAssessmentProperties = [
  'recommendation',
  'impressions',
  'goals',
  'discussionSummary',
  'signs',
  'symptoms',
  'medicalHistory',
  'medications',
  'pieCharts',
  'patientCharts'
];

const assessmentSimilarProperties = [
  SymptomModes.Advanced,
  SymptomModes.FollowUp,
  SymptomModes.Silver
];

function createAssessmentProperties() {
  assessmentSimilarProperties.forEach(assessmentType => {
    assessmentTypeProperty[assessmentType] = sharedAssessmentProperties;
  });
  assessmentTypeProperty[SymptomModes.Simple] = [
    'recommendation',
    'impressions',
    'signs',
    'symptoms',
    'pieCharts',
    'patientCharts'
  ];
  assessmentTypeProperty[SymptomModes.Treatment] = [
    'recommendation',
    'impressions',
    'goals',
    'discussionSummary',
    'signs',
    'symptoms',
    'medicalHistory',
    'medications',
    'treatmentPerformed',
    'pieCharts',
    'patientCharts'
  ];

  assessmentTypeProperty[SymptomModes.PreOpScreening] = [
    'recommendation',
    'impressions',
    'goals',
    'discussionSummary',
    'signs',
    'symptoms',
    'medications',
    'pieCharts',
    'patientCharts'
  ];
}

export interface Signs {
  [key: string]: {
    value: string;
    symptomKey: string;
    color: string;
  };
}